export interface GeocodeParts {
  street: string;
  streetNumber: string;
  colonia: string;
  city: string;
  state: string;
  zip: string;
}

interface NominatimHit {
  lat: string;
  lon: string;
}

function hasEnoughAddress(parts: {
  streetLine: string;
  colonia: string;
  city: string;
  state: string;
  zip: string;
}): boolean {
  if (parts.streetLine.length >= 3) {
    return true;
  }
  if (parts.colonia.length >= 2) {
    return true;
  }
  if (parts.city.length >= 2 && parts.state.length >= 2) {
    return true;
  }
  return /^\d{5}$/.test(parts.zip);
}

function formatColonia(colonia: string): string {
  const value = colonia.trim();
  if (!value) {
    return "";
  }
  return /^colonia\b/i.test(value) ? value : `Colonia ${value}`;
}

async function nominatimSearch(
  params: URLSearchParams,
): Promise<{ lat: number; lng: number } | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      {
        headers: {
          Accept: "application/json",
          "Accept-Language": "es",
        },
      },
    );

    if (!response.ok) {
      return null;
    }

    const results: NominatimHit[] = await response.json();
    const match = results[0];
    if (!match) {
      return null;
    }

    return {
      lat: parseFloat(match.lat),
      lng: parseFloat(match.lon),
    };
  } catch {
    return null;
  }
}

export async function geocodeAddress(
  parts: GeocodeParts,
): Promise<{ lat: number; lng: number } | null> {
  const street = (parts.street ?? "").trim();
  const streetNumber = (parts.streetNumber ?? "").trim();
  const streetLine = [street, streetNumber].filter(Boolean).join(" ");
  const city = (parts.city ?? "").trim();
  const state = (parts.state ?? "").trim();
  const colonia = (parts.colonia ?? "").trim();
  const zip = (parts.zip ?? "").trim();
  const coloniaLabel = formatColonia(colonia);

  if (
    !hasEnoughAddress({
      streetLine,
      colonia,
      city,
      state,
      zip,
    })
  ) {
    return null;
  }

  // Street present: use structured search so colonia is not treated as a road.
  if (streetLine.length >= 3) {
    const structured = new URLSearchParams({
      street: streetLine,
      country: "Mexico",
      format: "json",
      limit: "1",
    });
    if (city) {
      structured.set("city", city);
    }
    if (state) {
      structured.set("state", state);
    }
    if (/^\d{5}$/.test(zip)) {
      structured.set("postalcode", zip);
    }

    const structuredHit = await nominatimSearch(structured);
    if (structuredHit) {
      return structuredHit;
    }

    // Fallback free-text with explicit "Colonia …" label.
    const freeText = new URLSearchParams({
      q: [streetLine, coloniaLabel, city, state, zip, "México"]
        .filter(Boolean)
        .join(", "),
      format: "json",
      limit: "1",
      countrycodes: "mx",
    });
    return nominatimSearch(freeText);
  }

  // No street yet: locate by colonia / city / CP, never as a street name.
  if (coloniaLabel) {
    const coloniaQuery = new URLSearchParams({
      q: [coloniaLabel, city, state, zip, "México"].filter(Boolean).join(", "),
      format: "json",
      limit: "1",
      countrycodes: "mx",
    });
    const coloniaHit = await nominatimSearch(coloniaQuery);
    if (coloniaHit) {
      return coloniaHit;
    }
  }

  if (/^\d{5}$/.test(zip)) {
    const postalParams = new URLSearchParams({
      postalcode: zip,
      country: "Mexico",
      format: "json",
      addressdetails: "1",
      limit: "1",
    });
    const postalHit = await nominatimSearch(postalParams);
    if (postalHit) {
      return postalHit;
    }
  }

  if (city || state) {
    const localityQuery = new URLSearchParams({
      q: [city, state, zip, "México"].filter(Boolean).join(", "),
      format: "json",
      limit: "1",
      countrycodes: "mx",
    });
    return nominatimSearch(localityQuery);
  }

  return null;
}
